// src/pages/readerGrouping.js
//
// Pure, framework-free nav-grouping helpers for the shared grouped reader
// (PackReaderGrouped). Two strategies, both returning ordered groups
// [{ key, label, items }] (groups with zero items are dropped):
//   - groupByFixedOrder: a FIXED order + labels (literature: sectionKind via
//     SECTION_ORDER). Behaviour-identical to the old lit byKind + navSections.
//   - groupByDiscovered: a FREE-STRING key in author/first-seen order (skills:
//     techniques by content.blocks.unit; blank -> a single fallback group).
// JSX-free so they unit-test under `node --test`.

// items grouped by keyOf(item), emitted in the fixed `order` ([{key,label}]).
// Only groups that actually have items are returned, preserving item push order.
export function groupByFixedOrder(items, keyOf, order) {
    const byKey = {};
    for (const it of items || []) {
        if (!it) continue;
        const k = keyOf(it);
        (byKey[k] || (byKey[k] = [])).push(it);
    }
    return (order || [])
        .filter((o) => byKey[o.key] && byKey[o.key].length > 0)
        .map((o) => ({ key: o.key, label: o.label, items: byKey[o.key] }));
}

// Combined cross-scope ordering (section-ordering S1): sort an ALREADY-LOADED
// merged free+paid array by displayOrder so free and paid share ONE authored
// sequence. Non-mutating. `?? 999` matches usePackItems' fallback, and the
// stable sort means all-999 legacy items keep their input (free-then-paid)
// order -- so this is a visual NO-OP until the backfill assigns real numbers.
// GATING NOTE: this only ever sorts arrays already in memory; whether paid
// items were loaded at all remains decided by the strict entitlement gates.
export function byDisplayOrder(items) {
    return [...(items || [])].sort((a, b) => ((a?.displayOrder ?? 999) - (b?.displayOrder ?? 999)));
}

// items grouped by a free-string keyOf(item), in first-seen order. A blank/missing
// key folds into one fallback group (key '' , label fallbackLabel).
export function groupByDiscovered(items, keyOf, { fallbackLabel = 'Other' } = {}) {
    const order = [];
    const byKey = {};
    for (const it of items || []) {
        if (!it) continue;
        const raw = keyOf(it);
        const k = (typeof raw === 'string' && raw.trim()) ? raw.trim() : '';
        if (!(k in byKey)) { byKey[k] = []; order.push(k); }
        byKey[k].push(it);
    }
    return order.map((k) => ({ key: k, label: k || fallbackLabel, items: byKey[k] }));
}
